/**
 * Column Mapping Dialog
 * User-friendly interface for mapping CSV columns to transaction fields
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, Info } from "lucide-react";

interface ColumnMapping {
  date?: number;
  description?: number;
  amount?: number;
  credit?: number;
  debit?: number;
  balance?: number;
  reference?: number;
  bookingDate?: number;
  purchaseDate?: number;
  invoicingPeriod?: number;
}

interface ColumnMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  headerRow: string[];
  previewRows: string[][];
  suggestedMapping: ColumnMapping;
  onConfirm: (mapping: ColumnMapping) => void;
  onCancel: () => void;
}

const FIELD_LABELS: Record<
  keyof ColumnMapping,
  { label: string; required: boolean; description: string }
> = {
  date: { label: "Date", required: true, description: "Transaction date" },
  description: {
    label: "Description",
    required: true,
    description: "Transaction description",
  },
  amount: {
    label: "Amount",
    required: false,
    description: "Single amount column (if not using Credit/Debit)",
  },
  credit: {
    label: "Credit",
    required: false,
    description: "Credit amount (incoming)",
  },
  debit: {
    label: "Debit",
    required: false,
    description: "Debit amount (outgoing)",
  },
  balance: {
    label: "Balance",
    required: false,
    description: "Account balance after transaction",
  },
  reference: {
    label: "Reference",
    required: false,
    description: "Transaction reference number",
  },
  bookingDate: {
    label: "Booking Date",
    required: false,
    description: "When transaction was booked",
  },
  purchaseDate: {
    label: "Purchase Date",
    required: false,
    description: "When purchase was made",
  },
  invoicingPeriod: {
    label: "Invoicing Period",
    required: false,
    description: "Billing period",
  },
};

export function ColumnMappingDialog({
  open,
  onOpenChange,
  headerRow,
  previewRows,
  suggestedMapping,
  onConfirm,
  onCancel,
}: ColumnMappingDialogProps) {
  const [mapping, setMapping] = useState<ColumnMapping>(suggestedMapping);

  const updateMapping = (field: keyof ColumnMapping, value: string) => {
    setMapping(prev => ({
      ...prev,
      [field]: value === "none" ? undefined : parseInt(value),
    }));
  };

  const validateMapping = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Must have date
    if (mapping.date === undefined && mapping.bookingDate === undefined) {
      errors.push("Date column is required");
    }

    // Must have description
    if (mapping.description === undefined) {
      errors.push("Description column is required");
    }

    // Must have amount OR credit/debit
    if (
      mapping.amount === undefined &&
      (mapping.credit === undefined || mapping.debit === undefined)
    ) {
      errors.push(
        "Either Amount column OR both Credit and Debit columns are required"
      );
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  };

  const validation = validateMapping();

  const handleConfirm = () => {
    if (validation.valid) {
      onConfirm(mapping);
    }
  };

  // Get column value for preview
  const getColumnValue = (
    row: string[],
    field: keyof ColumnMapping
  ): string => {
    const colIndex = mapping[field];
    if (colIndex === undefined) return "-";
    return row[colIndex] || "-";
  };

  // Check if column is mapped
  const isColumnMapped = (colIndex: number): boolean => {
    return Object.values(mapping).includes(colIndex);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Map CSV Columns</DialogTitle>
          <DialogDescription>
            Match your CSV columns to the transaction fields. We've suggested
            mappings based on column names.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Validation Alerts */}
          {!validation.valid && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium mb-1">
                  Please fix the following issues:
                </div>
                <ul className="list-disc list-inside space-y-1">
                  {validation.errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {validation.valid && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Column mapping is valid and ready to import.
              </AlertDescription>
            </Alert>
          )}

          {/* Column Mapping Selectors */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Field Mappings</h3>
            <div className="grid grid-cols-2 gap-4">
              {(Object.keys(FIELD_LABELS) as Array<keyof ColumnMapping>).map(
                field => {
                  const fieldInfo = FIELD_LABELS[field];
                  return (
                    <div key={field} className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        {fieldInfo.label}
                        {fieldInfo.required && (
                          <Badge variant="destructive" className="text-xs">
                            Required
                          </Badge>
                        )}
                      </label>
                      <Select
                        value={mapping[field]?.toString() || "none"}
                        onValueChange={value => updateMapping(field, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select column..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <span className="text-muted-foreground">
                              Not mapped
                            </span>
                          </SelectItem>
                          {headerRow.map((header, index) => (
                            <SelectItem key={index} value={index.toString()}>
                              <div className="flex items-center gap-2">
                                <span>
                                  Column {index + 1}: {header || "(empty)"}
                                </span>
                                {isColumnMapped(index) &&
                                  mapping[field] !== index && (
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      In use
                                    </Badge>
                                  )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {fieldInfo.description}
                      </p>
                    </div>
                  );
                }
              )}
            </div>
          </div>

          {/* Preview Table */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Info className="h-4 w-4" />
              Preview (First 5 Rows)
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount/Credit</TableHead>
                    <TableHead>Debit</TableHead>
                    <TableHead>Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewRows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-sm">
                        {getColumnValue(row, "date") ||
                          getColumnValue(row, "bookingDate")}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {getColumnValue(row, "description")}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {getColumnValue(row, "amount") ||
                          getColumnValue(row, "credit")}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {getColumnValue(row, "debit")}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {getColumnValue(row, "balance")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Raw CSV Preview */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Raw CSV Data</h3>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {headerRow.map((header, i) => (
                      <TableHead key={i} className="min-w-[120px]">
                        <div className="space-y-1">
                          <div className="font-medium">Column {i + 1}</div>
                          <div className="text-xs font-normal text-muted-foreground truncate">
                            {header || "(empty)"}
                          </div>
                          {isColumnMapped(i) && (
                            <Badge variant="secondary" className="text-xs">
                              {
                                Object.entries(mapping).find(
                                  ([, v]) => v === i
                                )?.[0]
                              }
                            </Badge>
                          )}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewRows.map((row, i) => (
                    <TableRow key={i}>
                      {row.map((cell, j) => (
                        <TableCell key={j} className="font-mono text-xs">
                          {cell || "-"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!validation.valid}>
            Confirm Mapping & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
